import React, { useEffect, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail } from "lucide-react";

const DEFAULT_PRESETS = [100, 250, 500, 1000, 2000];

export const TopupRequestDialog = ({ dealer = null, initialAmount = "", label = "Richiedi ricarica via email", onSent = () => {}, testId = "topup-request" }) => {
  const [open, setOpen] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [userId, setUserId] = useState(dealer?.id || "");
  const [presets, setPresets] = useState(DEFAULT_PRESETS);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { return; }
    api.get("/admin/recharge-config").then((r) => { if (r.data?.presets?.length) setPresets(r.data.presets); }).catch(() => {});
    if (!dealer) api.get("/dealers", { params: { status: "approved" } }).then((r) => setDealers(r.data || [])).catch(() => {});
  }, [open, dealer]);
  const value = Number(amount);
  const valid = userId && value >= 10 && value <= 50000;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/topup-requests", { user_id: userId, amount: value.toFixed(2), note: note.trim() || null });
      data.email_sent ? toast.success(`Richiesta inviata a ${data.email}. La trovi in attesa di pagamento.`) : toast.warning("Richiesta registrata, ma l'email non è partita: riprova o avvisa il dealer.");
      setOpen(false); setAmount(""); setNote(""); onSent();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="gap-2" onClick={() => { setUserId(dealer?.id || ""); setAmount(initialAmount ? String(initialAmount) : ""); setOpen(true); }} data-testid={`${testId}-open`}><Mail className="w-4 h-4" /> {label}</Button>
      <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <DialogContent data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>Richiesta di ricarica Plafond servizi</DialogTitle>
            <DialogDescription>Email al dealer con importo e modi di pagamento; resta in attesa fino all'incasso.</DialogDescription>
          </DialogHeader>
          {dealer ? <p className="text-sm" data-testid={`${testId}-dealer-name`}>Destinatario: <strong>{dealer.company_name || dealer.name}</strong></p> : (
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid={`${testId}-dealer-select`}><SelectValue placeholder="Scegli dealer o master" /></SelectTrigger>
              <SelectContent className="max-h-72">{dealers.map((d) => <SelectItem key={d.id} value={d.id}>{d.company_name || d.name}{d.is_master ? " · Master" : ""}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testId}-presets`}>
            {presets.map((p) => <Button key={p} type="button" size="sm" className="sim-chip h-8" variant={value === Number(p) ? "default" : "outline"} onClick={() => setAmount(String(p))} data-testid={`${testId}-preset-${p}`}>{euro(p)}</Button>)}
            <Input type="number" min="10" max="50000" step="0.01" className="h-8 w-28" placeholder="Altro €" aria-label="Importo" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={`${testId}-amount`} />
          </div>
          <Textarea rows={2} className="min-h-0" placeholder="Nota nell'email (facoltativa)" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`${testId}-note`} />
          <Button disabled={!valid || busy} onClick={send} data-testid={`${testId}-send`}>{busy ? "Invio…" : valid ? `Invia richiesta di ${euro(value)}` : "Invia richiesta"}</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
